import { formatDistanceToNow } from 'date-fns';
import { Users } from 'lucide-react';
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import styles from './PresenceIndicator.module.css';

export type PresenceStatus = 'online' | 'idle' | 'offline' | 'viewing' | 'editing';

export interface UserPresence {
  email: string;
  status: PresenceStatus;
  lastSeen: number;
  currentNodeId?: string | null;
  action?: string;
}

interface PresenceIndicatorProps {
  activeUsers: Record<string, UserPresence> | UserPresence[];
  maxDisplayed?: number;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
  currentNodeId: string;
}

const getStatusColor = (status: PresenceStatus): string => {
  switch (status) {
    case 'online':
      return 'bg-green-500';
    case 'viewing':
      return 'bg-blue-500';
    case 'editing':
      return 'bg-purple-500';
    case 'idle':
      return 'bg-yellow-500';
    case 'offline':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
};

// Simple function to generate initials from email
const getInitials = (email: string): string => {
  if (!email) return '?';
  const namePart = email.split('@')[0];
  if (!namePart) return '?';
  
  const parts = namePart.split(/[._-]/);
  if (parts.length === 1) {
    return namePart.substring(0, 2).toUpperCase();
  }
  
  return parts
    .filter(part => part.length > 0)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');
};

// This component is designed to be reusable across the app
export const PresenceIndicator = React.memo(({
  activeUsers,
  maxDisplayed = 3,
  showDetails = true,
  size = 'md',
  currentNodeId,
}: PresenceIndicatorProps) => {
  // Convert activeUsers to array if it's an object
  const usersArray = Array.isArray(activeUsers) 
    ? activeUsers 
    : Object.values(activeUsers || {});
  
  // Filter users to those active in this node (if currentNodeId is provided)
  const relevantUsers = usersArray.filter(user => 
    !currentNodeId || user.currentNodeId === currentNodeId
  );
  
  if (!relevantUsers || relevantUsers.length === 0) return null;

  if (size === 'sm' || !showDetails) {
    return (
      <div className="flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-md text-xs">
        <Users size={12} className="mr-1" />
        <span>{relevantUsers.length}</span>
      </div>
    );
  }

  const displayedUsers = relevantUsers.slice(0, maxDisplayed);
  const remainingCount = relevantUsers.length - maxDisplayed;

  return (
    <div className={styles.container}>
      <div className={styles.avatarGroup}>
        {displayedUsers.map((user) => (
          <TooltipProvider key={user.email} delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={styles.avatarWrapper}>
                  <Avatar className={styles.avatar}>
                    <AvatarFallback>{getInitials(user.email)}</AvatarFallback>
                    <div className={`${styles.statusIndicator} ${getStatusColor(user.status)}`} />
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="p-1">
                  <div className="font-medium">{user.email}</div>
                  <div className="text-sm flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-1.5 ${getStatusColor(user.status)}`} />
                    <span>{user.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">
Last seen: {user.lastSeen && !isNaN(new Date(user.lastSeen).getTime())
  ? formatDistanceToNow(new Date(user.lastSeen), { addSuffix: true })
  : 'Unknown'}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
        {remainingCount > 0 && (
          <div className="bg-gray-200 dark:bg-gray-700 rounded-full px-2 py-1 ml-1 text-xs">
            +{remainingCount}
          </div>
        )}
      </div>
    </div>
  );
});

export default PresenceIndicator;